import { useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';

import { IFinancialAssistanceForm } from '@customTypes/requisition';
import useDebounce from '@hooks/useDebounce';

import { getEstimatedCost } from './HouseholdInformation.utils';

enum EstimatedCostCalcStatus {
  NOT_STARTED,
  IN_PROGRESS,
  COMPLETED,
  INELIGIBLE,
}

const HouseholdEstimatedCost = () => {
  const { values, setFieldValue } = useFormikContext<IFinancialAssistanceForm>();

  const [estimatedCostCalcStatus, setEstimatedCostCalcStatus] = useState<EstimatedCostCalcStatus>(
    EstimatedCostCalcStatus.NOT_STARTED,
  );
  const debouncedHouseholdIncome = useDebounce(values.household_income);

  const ineligible = useMemo(
    () => estimatedCostCalcStatus === EstimatedCostCalcStatus.INELIGIBLE,
    [estimatedCostCalcStatus],
  );
  const title: string | null = useMemo(() => {
    switch (estimatedCostCalcStatus) {
      case EstimatedCostCalcStatus.NOT_STARTED:
        return 'Complete household size and income to calculate your estimated cost.';
      case EstimatedCostCalcStatus.IN_PROGRESS:
        return 'Calculating...';
      case EstimatedCostCalcStatus.COMPLETED:
        return null;
      case EstimatedCostCalcStatus.INELIGIBLE:
      default:
        return 'It looks like your current household size and income don’t meet the qualifications for assistance.';
    }
  }, [estimatedCostCalcStatus]);

  useEffect(() => {
    if (!values.household_size || !debouncedHouseholdIncome) {
      setEstimatedCostCalcStatus(EstimatedCostCalcStatus.NOT_STARTED);
    } else {
      setEstimatedCostCalcStatus(EstimatedCostCalcStatus.IN_PROGRESS);
      setTimeout(() => {
        const cost = getEstimatedCost(values.household_size, debouncedHouseholdIncome);
        setFieldValue('estimated_max_cost', cost ?? 0);
        setEstimatedCostCalcStatus(
          cost === null ? EstimatedCostCalcStatus.INELIGIBLE : EstimatedCostCalcStatus.COMPLETED,
        );
      }, 500);
    }
  }, [values.household_size, setFieldValue, debouncedHouseholdIncome]);

  return (
    <div className={`p-3 estimate-section ${ineligible ? 'bg-danger-subtle text-danger-emphasis' : ''}`}>
      {!!title && <p className='mb-0 fs-sm fw-medium text-center'>{title}</p>}
      {ineligible ? (
        <p className='mb-0 fs-xs'>
          Please make sure you have entered your information correctly and contact us if you need further assistance.
        </p>
      ) : (
        <div className='d-flex justify-content-center align-items-center gap-3'>
          <p className='mb-0 fw-bold text-primary'>Estimated cost to you:</p>
          {estimatedCostCalcStatus === EstimatedCostCalcStatus.IN_PROGRESS ? (
            <Spinner animation='border' variant='primary' size='sm' />
          ) : (
            <span className='fs-6 fw-bold'>
              {estimatedCostCalcStatus === EstimatedCostCalcStatus.COMPLETED
                ? '$' + values.estimated_max_cost?.toFixed(2)
                : '----'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default HouseholdEstimatedCost;
